import React from "react";
import { AssessmentQuestion } from "./questionTypes";
import { AnswerData, QuestionAnswer } from "./answerTypes";
import { QuestionRenderer } from "./QuestionRenderer";

const ADVISOR_LABELS: Record<string, string> = {
  accountant_cpa: 'Accountant / CPA',
  tax_planning_specialist: 'Tax Planning Specialist',
  bookkeeper: 'Bookkeeper',
  cross_border_tax: 'Cross-Border Tax Advisor',
  cfo_fractional: 'CFO / Fractional CFO',
  other_financial: 'Other Financial / Tax Advisor',
  corporate_business_lawyer: 'Corporate / Business Lawyer',
  estate_planning_lawyer: 'Estate Planning Lawyer',
  tax_lawyer: 'Tax Lawyer',
  securities_regulatory_lawyer: 'Securities / Regulatory Lawyer',
  employment_lawyer: 'Employment Lawyer',
  ip_lawyer: 'Intellectual Property Lawyer',
  partnership_shareholder_specialist: 'Partnership / Shareholder Specialist',
  other_legal: 'Other Legal Advisor',
  business_valuator_cbv: 'Chartered Business Valuator (CBV)',
  ma_advisor: 'M&A Advisor',
  succession_consultant: 'Succession Planning Consultant',
  dealer_liaison: 'Dealer / Broker Succession Liaison',
  transition_coach: 'Business Transition Coach',
  other_succession: 'Other Succession Specialist',
  personal_financial_planner: 'Personal Financial Planner',
  insurance_advisor: 'Insurance Advisor',
  wealth_manager: 'Wealth Manager / Financial Planner',
  estate_trustee: 'Estate Trustee / Trust Officer',
  risk_management_consultant: 'Risk Management Consultant',
  other_wealth: 'Other Wealth / Risk Advisor',
};

const INLINE_PAIR_SUFFIXES = ['_name', '_title', '_email'];
const CONTACT_SUFFIXES = ['_name', '_title', '_email', '_firm', '_phone'];

interface AdvisorTypeSectionCardProps {
  advisorType: string;
  questions: AssessmentQuestion[];
  answers: AnswerData;
  onAnswerChange: (questionId: string, value: QuestionAnswer) => void;
  onNext: () => void;
  onFinish: () => void;
}

export function AdvisorTypeSectionCard({
  advisorType,
  questions,
  answers,
  onAnswerChange,
  onNext,
  onFinish,
}: AdvisorTypeSectionCardProps) {
  const label = ADVISOR_LABELS[advisorType] || advisorType.replace(/_/g, ' ');

  // Split questions into layout groups
  const nameEmailQuestions = questions.filter((q) =>
    INLINE_PAIR_SUFFIXES.some((suffix) => q.id.endsWith(suffix))
  );
  const firmQuestions = questions.filter((q) =>
    q.id.endsWith('_firm') || q.id.endsWith('_phone')
  );
  const qualityQuestions = questions.filter(
    (q) => !CONTACT_SUFFIXES.some((suffix) => q.id.endsWith(suffix))
  );

  return (
    <div className="rounded-lg bg-card/30 p-5 space-y-4">
      {/* Advisor type heading */}
      <h3 className="text-[15px] font-semibold text-foreground tracking-tight">
        {label}
      </h3>

      {/* Row 1: Name + Email side by side */}
      {nameEmailQuestions.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {nameEmailQuestions.map((q) => (
            <div key={q.id}>
              <QuestionRenderer
                question={q}
                answers={answers}
                onAnswerChange={onAnswerChange}
                onNext={onNext}
                onFinish={onFinish}
              />
            </div>
          ))}
        </div>
      )}

      {/* Row 2: Firm / Phone — full width */}
      {firmQuestions.map((q) => (
        <div key={q.id}>
          <QuestionRenderer
            question={q}
            answers={answers}
            onAnswerChange={onAnswerChange}
            onNext={onNext}
            onFinish={onFinish}
          />
        </div>
      ))}

      {/* Row 3+: Succession experience, Practice familiarity — full width */}
      {qualityQuestions.map((q) => (
        <div key={q.id}>
          <QuestionRenderer
            question={q}
            answers={answers}
            onAnswerChange={onAnswerChange}
            onNext={onNext}
            onFinish={onFinish}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Extracts the advisor type prefix from a question_key.
 * Returns null if the key does not match any known advisor type.
 */
export function getAdvisorTypeFromKey(questionKey: string): string | null {
  // Sort by length descending so longer prefixes match first
  const sortedKeys = Object.keys(ADVISOR_LABELS).sort(
    (a, b) => b.length - a.length
  );
  for (const prefix of sortedKeys) {
    if (questionKey.startsWith(prefix + '_')) {
      return prefix;
    }
  }
  return null;
}

export { ADVISOR_LABELS };
